// src/admin/AdminRankings.js
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import Api from "../../../../shared/api/api";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";
import "../styles/AdminRankings.css";

const MARKETPLACE_MAP = {
  com: "USA",
  "co.uk": "UK",
  ca: "Canada",
  "com.au": "Australia",
  in: "India",
  de: "Germany",
};
const MARKETPLACE_ORDER = ["com", "co.uk", "ca", "com.au", "in", "de"];
const FORMAT_ORDER = ["ebook", "paperback", "hardcover"];

function countryLabel(code) {
  return MARKETPLACE_MAP[code] || code;
}
function prettifyDate(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Normalize any category-like string for matching.
 * Strips punctuation that often appears around the label.
 */
function normalizeCat(s = "") {
  return String(s)
    .toLowerCase()
    .replace(/[–—\-():]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Detect if a category row represents the overall Best Sellers rank.
 * Handles many real-world variants:
 * - "best sellers rank", "bestsellers rank", "best seller rank", "bestseller rank"
 * - "best sellers rank (kindle store)", "(books)"
 * - plain "kindle store" or "books" rows that the scraper saved
 */
function isBestSellersCategory(cat = "") {
  const c = normalizeCat(cat);
  return (
    c.includes("best sellers rank") ||
    c.includes("bestsellers rank") ||
    c.includes("best seller rank") ||
    c.includes("bestseller rank") ||
    c === "kindle store" ||
    c === "books"
  );
}

/**
 * Human label for display.
 */
function displayBSRLabel(cat = "") {
  const c = normalizeCat(cat);
  if (c.includes("kindle")) return "Best Sellers Rank (Kindle Store)";
  if (c.includes("book") || c === "books") return "Best Sellers Rank (Books)";
  return "Best Sellers Rank";
}

export default function AdminRankings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [selectedFormat, setSelectedFormat] = useState("ebook");
  const [selectedMarketplace, setSelectedMarketplace] = useState("com"); // default USA

  // Load and normalize once
  useEffect(() => {
    (async () => {
      try {
        const res = await Api.getAllRankings();
        const data = (res.rankings || []).map((r) => ({
          ...r,
          format: (r.format || "").toLowerCase(),
          marketplace: (r.marketplace || "").toLowerCase(),
          category: (r.category || "").toLowerCase(),
          rank: Number(r.rank),
          rank_diff: Number(r.rank_diff ?? 0),
        }));
        setRows(data);
      } catch (e) {
        console.error(e);
        setErr("Failed to load rankings.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Group rows by (book, format, marketplace)
  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = `${r.book_title}__${r.format}__${r.marketplace}`;
      if (!map.has(key)) {
        map.set(key, {
          book_title: r.book_title,
          format: r.format,
          marketplace: r.marketplace,
          book_image: r.book_image || null,
          entries: [],
          latest_at: r.retrieved_at,
        });
      }
      const g = map.get(key);
      g.entries.push({
        category: r.category,
        rank: r.rank,
        rank_diff: r.rank_diff,
        retrieved_at: r.retrieved_at,
      });
      if (!g.latest_at || new Date(r.retrieved_at) > new Date(g.latest_at)) {
        g.latest_at = r.retrieved_at;
      }
    }
    return Array.from(map.values());
  }, [rows]);
  // Formats available across all markets
  const availableFormats = useMemo(
    () => Array.from(new Set(grouped.map((g) => g.format))),
    [grouped]
  );

  // Markets available for the currently selected format
  const availableMarkets = useMemo(() => {
    return Array.from(
      new Set(
        grouped
          .filter((g) => g.format === selectedFormat)
          .map((g) => g.marketplace)
      )
    );
  }, [grouped, selectedFormat]);

  // Fallbacks for invalid selection
  useEffect(() => {
    if (availableFormats.length && !availableFormats.includes(selectedFormat)) {
      setSelectedFormat(availableFormats[0]);
    }
  }, [availableFormats, selectedFormat]);

  useEffect(() => {
    if (availableMarkets.length && !availableMarkets.includes(selectedMarketplace)) {
      setSelectedMarketplace(availableMarkets[0]);
    }
  }, [availableMarkets, selectedMarketplace]);

  /**
   * For each (book,format,market), pick the most recent overall Best Sellers rank.
   * Priority: explicit Best Sellers labels > plain "kindle store"/"books".
   * If multiple BSR entries exist, pick the newest by retrieved_at.
   */
  const visibleCards = useMemo(() => {
    return grouped
      .filter((g) => g.format === selectedFormat)
      .filter((g) => g.marketplace === selectedMarketplace)
      .map((g) => {
        const candidates = g.entries.filter((e) => isBestSellersCategory(e.category));
        // newest first
        candidates.sort(
          (a, b) => new Date(b.retrieved_at) - new Date(a.retrieved_at)
        );
        // Prefer explicit "best sellers" strings over plain roots
        let chosen = null;
        for (const e of candidates) {
          const c = normalizeCat(e.category);
          if (
            c.includes("best sellers rank") ||
            c.includes("bestsellers rank") ||
            c.includes("best seller rank") ||
            c.includes("bestseller rank")
          ) {
            chosen = e;
            break;
          }
        }
        if (!chosen) chosen = candidates[0] || null;

        return {
          ...g,
          bestSellers: chosen
            ? {
              label: displayBSRLabel(chosen.category),
              rank: chosen.rank,
              diff: chosen.rank_diff,
            }
            : null,
        };
      })
      .filter((g) => g.bestSellers && Number.isFinite(g.bestSellers.rank))
      .sort((a, b) => a.book_title.localeCompare(b.book_title));
  }, [grouped, selectedFormat, selectedMarketplace]);

  return (
    <AdminLayout>
      <div className="rankingsContainer">
        <h2 className="rankingsHeader">Amazon Rankings</h2>

        {/* Filters */}
        <div className="filterRows">
          <div className="filterRow">
            <span className="filterLabel">Format:</span>
            {FORMAT_ORDER.map((fmt) => (
              <button
                key={fmt}
                className={`filterBtn ${selectedFormat === fmt ? "active" : ""}`}
                onClick={() => setSelectedFormat(fmt)}
                disabled={!availableFormats.includes(fmt)}
              >
                {fmt.charAt(0).toUpperCase() + fmt.slice(1)}
              </button>
            ))}
          </div>

          <div className="filterRow">
            <span className="filterLabel">Country:</span>
            {MARKETPLACE_ORDER.map((code) => (
              <button
                key={code}
                className={`filterBtn ${selectedMarketplace === code ? "active" : ""}`}
                onClick={() => setSelectedMarketplace(code)}
                disabled={!availableMarkets.includes(code)}
              >
                {countryLabel(code)}
              </button>
            ))}
          </div>
        </div>

        {loading && <p className="loadingText">Loading rankings…</p>}
        {err && <p className="errorText">{err}</p>}

        {!loading && !err && (
          visibleCards.length === 0 ? (
            <p className="loadingText">No Best Sellers ranks for the selected filters.</p>
          ) : (
            <div className="cardsGrid">
              {visibleCards.map((g, idx) => {
                const { label, rank, diff } = g.bestSellers;
                const diffClass = diff < 0 ? "up" : diff > 0 ? "down" : "same";

                return (
                  <article className="rankCard" key={`${g.book_title}-${idx}`}>
                    {/* Top: cover + title/badges */}
                    <div className="cardTop">
                      <div className="imageWrapper">
                        <img
                          src={g.book_image || ""}
                          alt={g.book_title}
                          className="cardCover"
                        />
                        {/* INFO ABOVE RANK */}
                        <div className="rankInfoBlock">
                          <div className="rankCategory">🥇 {label}</div>
                          <div className="rankRow">
                            <span className="rankVal large">#{Number(rank).toLocaleString()}</span>
                            <span className={`rankDiff ${diffClass}`}>
                              {diff < 0 ? <ArrowUp size={16} /> : diff > 0 ? <ArrowDown size={16} /> : <Minus size={16} />}
                              <span className="diffNum">
                                {diff === 0 ? "—" : `${diff < 0 ? "+" : "-"}${Math.abs(diff)}`}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="cardMeta">
                        <h3 className="cardTitle">
                          <Link
                            to={`/admin/rankings/${encodeURIComponent(g.book_title)}`}
                            className="cardTitleLink"
                          >
                            {g.book_title}
                          </Link>
                        </h3>
                        <div className="cardBadges">
                          <span className="badge">{g.format}</span>
                          <span className="badge">{countryLabel(g.marketplace)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Footer with centered button */}
                    <div className="cardFooter center">
                      <span className="updatedAt">
                        Last Updated: {prettifyDate(g.latest_at)}
                      </span>
                      <Link
                        to={`/admin/rankings/${encodeURIComponent(g.book_title)}`}
                        className="detailsLink centerButton"
                      >
                        View Details
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )
        )}
      </div>
    </AdminLayout>
  );
}
